// Upload status for one folder, in two layers:
//
//  • Default — plain words for a photographer at an event (src/lib/uploader/simpleStatus.ts): one short
//    sentence ("Uploading — about 2 min left", "Done — 5,001 photos live") with at most one button, and a
//    row per stage (Previews 3,214 / 5,001 · Originals 1,200 / 5,001) — never one total across both.
//  • "Show details" (collapsed by default, remembered per browser) — the detailed timeline drawn from
//    src/lib/uploader/uploadTimeline.ts: a swimlane per stage on a shared elapsed-time axis with a "now"
//    line on desktop, the same stages stacked vertically on a phone, with rates, ETAs and overlaps.
//  • The floating upload pill shows the same plain-words status, compactly.
//
// State is always spelled out in text (and icons), never colour alone; animations are motion-safe only.
import { useEffect, useId, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowUpToLine,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock,
  Hourglass,
  Info,
  Loader2,
  LogIn,
  MinusCircle,
  Pause,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  axisPct,
  buildTimeline,
  buildTimelines,
  fmtCount,
  fmtDuration,
  fmtElapsed,
  fmtRate,
  groupPipelines,
  pickTimeline,
  type StageState,
  type TimelineJob,
  type TimelineStage,
  type UploadTimeline as Timeline,
} from "@/lib/uploader/uploadTimeline";
import { simpleStatus, type SimpleActionKind, type SimpleStatus, type StageRow } from "@/lib/uploader/simpleStatus";
import { Button } from "@/components/ui/button";

// ---- state styling ---------------------------------------------------------------------------------

const BAR: Record<StageState, string> = {
  active: "bg-primary",
  paused: "bg-amber-500",
  waiting: "bg-amber-500/60",
  done: "bg-emerald-500 dark:bg-emerald-600",
  failed: "bg-destructive",
  cancelled: "bg-muted-foreground/40",
  skipped: "bg-muted-foreground/25",
  pending: "bg-muted-foreground/25",
};

const ICON_TONE: Record<StageState, string> = {
  active: "text-primary",
  paused: "text-amber-600 dark:text-amber-400",
  waiting: "text-amber-600 dark:text-amber-400",
  done: "text-emerald-600 dark:text-emerald-500",
  failed: "text-destructive",
  cancelled: "text-muted-foreground",
  skipped: "text-muted-foreground",
  pending: "text-muted-foreground",
};

export const StateIcon = ({ state, className }: { state: StageState; className?: string }) => {
  const cls = cn("h-3.5 w-3.5 shrink-0", ICON_TONE[state], className);
  switch (state) {
    case "active":
      return <Loader2 className={cn(cls, "motion-safe:animate-spin")} aria-hidden />;
    case "paused":
      return <Pause className={cls} aria-hidden />;
    case "waiting":
      return <Hourglass className={cls} aria-hidden />;
    case "done":
      return <CheckCircle2 className={cls} aria-hidden />;
    case "failed":
      return <AlertCircle className={cls} aria-hidden />;
    case "cancelled":
      return <Ban className={cls} aria-hidden />;
    case "skipped":
      return <MinusCircle className={cls} aria-hidden />;
    default:
      return <Circle className={cls} aria-hidden />;
  }
};

// ---- text helpers ----------------------------------------------------------------------------------

const rateText = (s: TimelineStage) =>
  !s.rate ? "" : s.rateUnit === "photos" ? `${fmtCount(s.rate)} photos/min` : fmtRate(s.rate);

/** The live figures for a stage: "64% · 3,214/5,001 · 12.4 MB/s · 2m 10s left". */
const figures = (s: TimelineStage): string => {
  if (s.milestone) return "";
  if (s.state === "done") {
    const took = s.start != null && s.end != null ? fmtDuration((s.end - s.start) / 1000) : "";
    return [s.total ? fmtCount(s.done) : "", took ? `in ${took}` : ""].filter(Boolean).join(" ");
  }
  if (!s.total) return "";
  const eta = fmtDuration(s.etaSec);
  return [s.summary, rateText(s), eta ? `${eta} left` : ""].filter(Boolean).join(" · ");
};

const stageAria = (s: TimelineStage) =>
  [s.label, s.stateLabel, figures(s), s.note].filter(Boolean).join(". ");

const overallState = (tl: Timeline): StageState =>
  tl.signedOut ? "waiting"
  : tl.paused ? "paused"
  : tl.complete ? "done"
  : tl.cancelled ? "cancelled"
  : tl.running ? "active"
  : tl.stages.some((s) => s.state === "failed") ? "failed"
  : "done";

const PARALLEL_NAME: Record<string, string> = {
  previews: "the previews",
  scan: "the face & people search",
  originals: "the originals",
  upload: "the upload",
};

// ---- swimlane (desktop) ----------------------------------------------------------------------------

const Lane = ({ tl, s }: { tl: Timeline; s: TimelineStage }) => {
  const nowPct = axisPct(tl, tl.now);
  const hasSpan = !s.milestone && s.start != null;
  const left = hasSpan ? axisPct(tl, s.start!) : 0;
  const right = hasSpan ? axisPct(tl, s.end ?? tl.now) : 0;
  const projEnd = hasSpan && s.end == null && s.etaSec != null ? axisPct(tl, tl.now + s.etaSec * 1000) : null;
  return (
    <div className="relative h-4" aria-hidden>
      <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
      {hasSpan && (
        <div
          className={cn("absolute top-0.5 bottom-0.5 rounded-sm motion-safe:transition-[left,width] motion-safe:duration-500", BAR[s.state])}
          style={{ left: `${left}%`, width: `max(3px, ${right - left}%)` }}
        />
      )}
      {projEnd != null && projEnd > right && (
        <div
          className="absolute top-0.5 bottom-0.5 rounded-sm border border-dashed border-primary/60 bg-primary/10"
          style={{ left: `${right}%`, width: `${projEnd - right}%` }}
        />
      )}
      {s.priority?.map((p, i) =>
        p.kind === "batch" ? (
          <div
            key={i}
            className="absolute -top-0.5 h-1.5 rounded-sm bg-violet-500 ring-1 ring-background"
            style={{ left: `${axisPct(tl, p.start)}%`, width: `max(4px, ${axisPct(tl, p.end ?? tl.now) - axisPct(tl, p.start)}%)` }}
          />
        ) : (
          <div
            key={i}
            className="absolute -top-0.5 -bottom-0.5 w-1 -translate-x-1/2 rounded-sm bg-violet-500 ring-1 ring-background"
            style={{ left: `${axisPct(tl, p.start)}%` }}
          />
        ),
      )}
      {s.milestone && s.at != null && (
        <div
          className={cn(
            "absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border",
            s.state === "done" ? "border-emerald-600 bg-emerald-500"
            : s.state === "failed" ? "border-destructive bg-destructive"
            : s.state === "waiting" ? "border-amber-600 bg-amber-200 dark:bg-amber-900"
            : "border-muted-foreground/60 bg-background",
          )}
          style={{ left: `${axisPct(tl, s.at)}%` }}
        />
      )}
      {(s.state === "waiting" && !hasSpan) && (
        <Hourglass
          className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 bg-background text-amber-600 dark:text-amber-400"
          style={{ left: `${nowPct}%` }}
        />
      )}
      {tl.running && <div className="absolute -top-0.5 -bottom-0.5 w-px bg-foreground/60" style={{ left: `${nowPct}%` }} />}
    </div>
  );
};

const Swimlane = ({ tl }: { tl: Timeline }) => {
  const nowPct = axisPct(tl, tl.now);
  const span = tl.axisEnd - tl.startedAt;
  const hasPriority = tl.stages.some((s) => s.priority?.length);
  const hasProjection = tl.running && tl.stages.some((s) => !s.milestone && s.end == null && s.etaSec != null);
  return (
    <div className="mt-1.5 grid grid-cols-[minmax(8.5rem,11rem)_minmax(6rem,1fr)_minmax(9rem,17rem)] items-center gap-x-3 gap-y-1">
      {/* "Now" marker label over the lanes. */}
      <div />
      <div className="relative h-3 text-[10px] text-muted-foreground" aria-hidden>
        {tl.running && (
          <span className="absolute -translate-x-1/2 font-medium text-foreground" style={{ left: `${Math.min(96, Math.max(4, nowPct))}%` }}>
            Now
          </span>
        )}
      </div>
      <div />
      {/* Subgrid (not display:contents) so each list item stays in the accessibility tree. */}
      <ol className="col-span-3 grid grid-cols-subgrid gap-y-1">
        {tl.stages.map((s) => (
          <li key={s.key} className="col-span-3 grid grid-cols-subgrid items-center" aria-label={stageAria(s)}>
            <span className="flex min-w-0 items-center gap-1.5">
              <StateIcon state={s.state} />
              <span className="truncate font-medium text-foreground">{s.label}</span>
            </span>
            <Lane tl={tl} s={s} />
            <span className="min-w-0 leading-tight" title={s.note}>
              <span className="block truncate">
                <span className={cn("font-medium", ICON_TONE[s.state])}>{s.stateLabel}</span>
                {figures(s) && <span className="tabular-nums text-muted-foreground"> · {figures(s)}</span>}
              </span>
              {s.note && <span className="block truncate text-[11px] text-muted-foreground">{s.note}</span>}
            </span>
          </li>
        ))}
      </ol>
      {/* Elapsed-time axis. */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        {hasPriority && (
          <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-3 rounded-sm bg-violet-500" aria-hidden /> Sold first</span>
        )}
        {hasProjection && (
          <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-3 rounded-sm border border-dashed border-primary/60" aria-hidden /> Expected</span>
        )}
      </div>
      <div className="relative h-3 text-[10px] tabular-nums text-muted-foreground" aria-hidden>
        <span className="absolute left-0">0:00</span>
        <span className="absolute left-1/2 -translate-x-1/2">{fmtElapsed(span / 2)}</span>
        <span className="absolute right-0">{fmtElapsed(span)}</span>
      </div>
      <div className="text-[10px] text-muted-foreground">Time since you started</div>
    </div>
  );
};

// ---- vertical timeline (phone) ---------------------------------------------------------------------

const VerticalTimeline = ({ tl }: { tl: Timeline }) => (
  <ol className="mt-2 space-y-0">
    {tl.stages.map((s, i) => {
      const last = i === tl.stages.length - 1;
      const fig = figures(s);
      return (
        <li key={s.key} className="relative pb-3 pl-7 last:pb-0" aria-label={stageAria(s)}>
          {!last && <span className="absolute bottom-0 left-[9px] top-5 w-px bg-border" aria-hidden />}
          <span
            className={cn(
              "absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full border bg-background",
              s.milestone && "rotate-45 rounded-sm",
            )}
            aria-hidden
          >
            <StateIcon state={s.state} className={cn("h-3 w-3", s.milestone && "-rotate-45")} />
          </span>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-foreground">{s.label}</span>
            <span className={cn("text-[11px] font-medium", ICON_TONE[s.state])}>{s.stateLabel}</span>
          </div>
          {fig && <div className="tabular-nums text-[11px] text-muted-foreground">{fig}</div>}
          {!s.milestone && s.total > 0 && s.pct != null && s.state !== "done" && (
            <div className="mt-1 h-1 w-full overflow-hidden rounded bg-muted">
              <div className={cn("h-full motion-safe:transition-[width] motion-safe:duration-500", BAR[s.state])} style={{ width: `${s.pct}%` }} />
            </div>
          )}
          {s.priority?.length ? (
            <div className="mt-1 flex items-center gap-1 text-[11px] text-foreground">
              <ArrowUpToLine className="h-3 w-3 text-violet-500" aria-hidden />
              {fmtCount(s.priority.reduce((n, p) => n + p.count, 0))} sold {s.priority.reduce((n, p) => n + p.count, 0) === 1 ? "photo" : "photos"} sent first
            </div>
          ) : null}
          {s.note && <div className="text-[11px] text-muted-foreground">{s.note}</div>}
          {s.parallelWith?.length && (s.state === "active" || s.state === "paused") ? (
            <div className="mt-0.5 text-[11px] italic text-muted-foreground">
              At the same time as {s.parallelWith.map((k) => PARALLEL_NAME[k] ?? k).join(" and ")}
            </div>
          ) : null}
        </li>
      );
    })}
  </ol>
);

// ---- composed --------------------------------------------------------------------------------------

/** The detailed timeline: swimlane from `sm` up, vertical below (or always vertical in narrow hosts). */
export const UploadTimeline = ({ timeline, layout = "auto" }: { timeline: Timeline; layout?: "auto" | "vertical" }) =>
  layout === "vertical" ? (
    <VerticalTimeline tl={timeline} />
  ) : (
    <>
      <div className="hidden sm:block">
        <Swimlane tl={timeline} />
      </div>
      <div className="sm:hidden">
        <VerticalTimeline tl={timeline} />
      </div>
    </>
  );

/** Ticks once a second while `active`, so elapsed time, ETAs and the "now" line keep moving even when
 *  no job state changes (e.g. paused, or waiting to sign in). */
const useNow = (active: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
};

/** navigator.onLine, kept live — offline while uploading reads "Paused — reconnecting". */
const useOnline = () => {
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
};

// "Show details" — collapsed by default, remembered per browser. (A new key: the old "timeline open"
// flag defaulted to open on desktop, which would show everyone the details again.)
const DETAILS_KEY = "ph_upload_timeline_details";
const readDetails = (): boolean => {
  try {
    return window.localStorage.getItem(DETAILS_KEY) === "1";
  } catch {
    return false;
  }
};
const writeDetails = (v: boolean) => {
  try {
    window.localStorage.setItem(DETAILS_KEY, v ? "1" : "0");
  } catch {
    /* storage unavailable — the toggle just isn't remembered */
  }
};

const isLive = (j: TimelineJob) => j.phase === "presigning" || j.phase === "uploading" || j.phase === "scanning";

const STATE_WORD: Record<StageState, string> = {
  active: "in progress",
  paused: "paused",
  waiting: "on hold",
  done: "done",
  failed: "failed",
  cancelled: "stopped",
  skipped: "not happening",
  pending: "next",
};

/** A coarse, transition-only status for screen readers (no per-file counts, so it isn't chatty). */
const announce = (s: SimpleStatus) =>
  [s.sentence.replace(/…$/, ""), ...s.rows.map((r) => `${r.label}: ${STATE_WORD[r.state]}`)].join(". ");

/** What the one button does. Without a handler the button isn't shown (the sentence still says it). */
export interface SimpleActions {
  onRetry?: () => void;
  onResume?: () => void;
  onSignIn?: () => void;
  onAddAgain?: () => void;
}
const handlerFor = (kind: SimpleActionKind, a: SimpleActions) =>
  kind === "retry" ? a.onRetry : kind === "resume" ? a.onResume : kind === "sign-in" ? a.onSignIn : a.onAddAgain;

/** The sentence's icon: the overall state, or a warning for a problem. */
export const StatusIcon = ({ status, className }: { status: SimpleStatus; className?: string }) =>
  status.tone === "waiting" && status.problem ? (
    <LogIn className={cn("h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400", className)} aria-hidden />
  ) : (
    <StateIcon state={status.tone} className={cn("h-4 w-4", className)} />
  );

/** One row per stage — "Previews ▬▬▬ 3,214 / 5,001" — counted in photos, never added together. */
export const StageRows = ({ rows, className }: { rows: readonly StageRow[]; className?: string }) => (
  <ul className={cn("max-w-xl space-y-1", className)} aria-label="Upload progress">
    {rows.map((r) => (
      <li key={r.key} className="grid grid-cols-[4.5rem_minmax(2rem,1fr)_auto] items-center gap-x-2">
        <span className="text-muted-foreground">{r.label}</span>
        <span className="h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
          <span
            className={cn("block h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500", BAR[r.state])}
            style={{ width: `${r.state === "pending" || r.state === "skipped" ? 0 : Math.max(r.done > 0 ? 2 : 0, r.pct)}%` }}
          />
        </span>
        <span className="min-w-0 truncate tabular-nums text-foreground" title={r.note}>
          {fmtCount(r.done)} / {fmtCount(r.total)}
          <span className="sr-only">: {STATE_WORD[r.state]}</span>
          {r.note && <span className={cn("text-muted-foreground", r.state === "failed" && "text-destructive")}> · {r.note}</span>}
        </span>
      </li>
    ))}
  </ul>
);

/** The sold-first hint, under the rows. */
const Hint = ({ text }: { text?: string }) =>
  text ? (
    <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
      <ArrowUpToLine className="h-3 w-3 shrink-0 text-violet-500" aria-hidden />
      {text}
    </p>
  ) : null;

/** The stage rows and the sold-first hint (the sentence and button are the caller's). */
export const SimpleSteps = ({ status, compact }: { status: SimpleStatus; compact?: boolean }) => (
  <>
    <StageRows rows={status.rows} className={compact ? "mt-1" : "mt-1.5"} />
    <Hint text={status.hint} />
  </>
);

/** Every pipeline in `jobs`, newest first — `published` applies only to `publishedFor`'s gallery when
 *  given (the upload app's queue spans galleries; the flag is the open gallery's). */
const panelTimelines = (
  jobs: readonly TimelineJob[],
  now: number,
  published: boolean | undefined,
  autoPublishArmed: boolean | undefined,
  publishedFor: string | undefined,
): Timeline[] =>
  groupPipelines(jobs)
    .map((g) => {
      const applies = !publishedFor || g[0].engineGuid === publishedFor;
      return buildTimeline(g, { now, published: applies ? published : undefined, autoPublishArmed: applies ? autoPublishArmed : undefined });
    })
    .filter((t): t is Timeline => !!t)
    .sort((a, b) => b.startedAt - a.startedAt);

interface PanelProps extends SimpleActions {
  jobs: readonly TimelineJob[];
  /** The folder being viewed — its upload is featured when it's running. */
  folder?: string;
  published?: boolean;
  autoPublishArmed?: boolean;
  /** The gallery `published` / `autoPublishArmed` describe (default: every job's). */
  publishedFor?: string;
  /** Say which gallery (the cross-gallery upload app) — when it isn't the one open (`publishedFor`). */
  showGallery?: boolean;
  /** Ignored — "Show details" is collapsed by default and remembered per browser. */
  defaultOpen?: boolean;
  className?: string;
}

/** The featured folder's timeline and its plain-words status (shared by the panel and the queue header). */
export const useUploadStatus = ({
  jobs, folder, published, autoPublishArmed, publishedFor,
}: Pick<PanelProps, "jobs" | "folder" | "published" | "autoPublishArmed" | "publishedFor">) => {
  const anyLive = jobs.some(isLive);
  const now = useNow(anyLive);
  const online = useOnline();
  const timelines = useMemo(
    () => panelTimelines(jobs, now, published, autoPublishArmed, publishedFor),
    [jobs, now, published, autoPublishArmed, publishedFor],
  );
  const tl = pickTimeline(timelines, folder);
  const others = tl ? timelines.filter((t) => t !== tl && t.running).length : 0;
  const applies = !!tl && (!publishedFor || tl.engineGuid === publishedFor);
  const status = useMemo(
    () => (tl ? simpleStatus(tl, { online, published: applies ? published : undefined, autoPublishArmed: applies ? autoPublishArmed : undefined }) : null),
    [tl, online, applies, published, autoPublishArmed],
  );
  return { tl, status, others };
};

/** The status above the transfer queue: one short sentence (with at most one button), a row per stage,
 *  and everything else — why, what's next, the detailed timeline — behind "Show details". */
export const UploadTimelinePanel = ({
  jobs,
  folder,
  published,
  autoPublishArmed,
  publishedFor,
  showGallery,
  className,
  onRetry,
  onResume,
  onSignIn,
  onAddAgain,
}: PanelProps) => {
  const { tl, status, others } = useUploadStatus({ jobs, folder, published, autoPublishArmed, publishedFor });
  const [details, setDetails] = useState(readDetails);
  const detailsId = useId();
  if (!tl || !status) return null;
  const toggle = () => setDetails((o) => { writeDetails(!o); return !o; });
  const run = status.action ? handlerFor(status.action.kind, { onRetry, onResume, onSignIn, onAddAgain }) : undefined;
  const notes = [...status.notes, ...(others > 0 ? [`${others} more ${others === 1 ? "folder is" : "folders are"} uploading too.`] : [])];
  return (
    <section className={cn("px-3 py-2 text-xs", className)} aria-label="Upload status">
      <div className="flex min-h-7 items-center gap-2">
        <StatusIcon status={status} />
        <p className="min-w-0 flex-1 line-clamp-2 text-sm font-medium leading-snug text-foreground" title={status.sentence}>
          {status.problem && <span className="sr-only">Problem: </span>}
          {showGallery && tl.engineGuid !== publishedFor && <span className="font-normal text-muted-foreground">{tl.galleryName} · </span>}
          {status.sentence}
        </p>
        {run && status.action && (
          <Button size="sm" variant="outline" className="h-7 shrink-0 rounded-md border px-2.5 text-xs font-medium" onClick={run}>
            {status.action.label}
          </Button>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={details}
          aria-controls={details ? detailsId : undefined}
          className="flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {details ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
          {details ? "Hide details" : "Show details"}
        </button>
      </div>
      <SimpleSteps status={status} />
      {details && (
        <div id={detailsId} className="mt-2 space-y-1.5 border-t pt-2" role="group" aria-label="Upload details">
          {notes.length > 0 && (
            <ul className="space-y-0.5 text-foreground">
              {notes.map((n) => (
                <li key={n} className="flex items-start gap-1.5">
                  <Info className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  {n}
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-x-2 text-muted-foreground">
            <span className="font-medium text-foreground">{tl.headline}</span>
            {tl.reason && <span className="min-w-0">— {tl.reason}</span>}
            <span className="ml-auto flex items-center gap-1 tabular-nums">
              <Clock className="h-3 w-3" aria-hidden />
              {fmtElapsed((tl.running ? tl.now : tl.axisEnd) - tl.startedAt)}
              <span className="sr-only"> elapsed</span>
            </span>
          </div>
          <UploadTimeline timeline={tl} />
        </div>
      )}
      <p className="sr-only" aria-live="polite">{announce(status)}</p>
    </section>
  );
};

/** The floating upload pill's version: the newest running preview-first folder, in the same plain words. */
export const UploadTimelineMini = ({ jobs }: { jobs: readonly TimelineJob[] }) => {
  const anyLive = jobs.some(isLive);
  const now = useNow(anyLive);
  const online = useOnline();
  const tl = useMemo(
    () => buildTimelines(jobs, { now }).find((t) => t.running && t.kind !== "plain") ?? null,
    [jobs, now],
  );
  const status = useMemo(() => (tl ? simpleStatus(tl, { online }) : null), [tl, online]);
  if (!tl || !status) return null;
  return (
    <div className="border-b border-border px-3 py-2 text-xs" aria-label={`Upload status — ${tl.galleryName}`} role="group">
      <p className="font-medium leading-snug text-foreground">{status.sentence}</p>
      <SimpleSteps status={status} compact />
    </div>
  );
};

export default UploadTimelinePanel;
